import { Navigate, RouteObject } from "react-router-dom";
import { RequirementLoader } from "./components/requirements/RequirementView";
import { RunReportPage } from "./components/runReports/RunReportPage";
import { RunReportVersionPage } from "./components/runReports/RunReportVersionPage";
import { ProcedureItemLoader } from "./components/procedures/ProcedureItemLoader";
import { ProcedureItem } from "./components/procedures/ProcedureItem";
import { ProcedureRun } from "./components/procedures/ProcedureRun";
import { FusaOrganizationCards } from "./components/cards/FusaOrganizationCards";
import { FusaOrganizationPage } from "./components/fusaOrganizations/FusaOrganizationPage";
import { LibraryPage } from "./components/library/LibraryPage";
import { AllWorkspaces } from "./components/workspaces/AllWorkspaces";
import { AllProjects } from "./components/projects/AllProjects";
import { RecentProjects } from "./components/projects/RecentProjects";
import { WorkspacePage } from "./components/workspaces/WorkspacePage";
import { ProjectPage } from "./components/projects/ProjectPage";
import { ProjectRequirementsList } from "./components/projects/ProjectRequirementsList";
import { ProjectPartsList } from "./components/projects/ProjectPartsList";
import { ProjectMilestonesList } from "./components/projects/ProjectMilestonesList";
import { ProjectSamplePhasesList } from "./components/projects/ProjectSamplePhasesList";
import { ProjectTemplatesPage } from "./components/projects/ProjectTemplatesPage";
import { PartView } from "./components/parts/PartView";
import { ProcedureImplementationSetupWizardLoader } from "./components/procedures/ProcedureImplementationSetupWizard";
import { ProcedureCreateLoader } from "./components/procedures/ProcedureCreate";
import { ProcedureList } from "./components/procedures/ProcedureList";
import { FusawarePermission } from "./fusawareUser";
import { NotFoundWrapper } from "./pages/NotFound";
import { SupportDocumentationTable } from "./components/supportDocumentation/SupportDocumentationTable";
import { ProjectPageLoader } from "./components/projects/ProjectPageLoader";
import { ProductPageLoader } from "./components/productPages/ProductPage";
import { ProductPageSectionType } from "@/backend/access/productPageSection/types";
import { ProjectTasksPage } from "./components/projects/ProjectTasksPage";
import { ReportStudioListPage } from "./components/reportStudio/ReportStudioListPage";
import { ReportStudioEditorPage } from "./components/reportStudio/ReportStudioEditorPage";
import { SchemaEditorPage } from "./components/reportStudio/schemas/SchemaEditorPage";
import { ReportV2ListPage } from "./components/reportsV2/ReportV2ListPage";
import { ReportV2FillPage } from "./components/reportsV2/ReportV2FillPage";
import { ReportV2PreviewPage, ReportV2VersionPage } from "./components/reportsV2/ReportV2PreviewPage";
import { AiAssistantPage } from "./components/mcp/AiAssistantPage";
import { AiApprovalsPage } from "./components/mcp/AiApprovalsPage";

export const routes: RouteObject[] = [
  {
    path: "/ai-assistant",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_AI_ASSISTANT}><AiAssistantPage /></NotFoundWrapper>
  },
  {
    path: "/ai-approvals",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_AI_ASSISTANT}><AiApprovalsPage /></NotFoundWrapper>
  },
  {
    path: "/home",
    element: <RecentProjects />
  },
  {
    path: "/",
    element: <RecentProjects />
  },
  {
    // FuSa organization requirements live here; a project's requirement redirects
    // to /projects/:projectId/requirements/:requirementId once it has loaded.
    path: "/requirements/:id",
    element: <RequirementLoader />
  },
  {
    path: "/runs/:runId/report",
    element: <RunReportPage />
  },
  {
    path: "/runs/:runId/report/versions/:versionId",
    element: <RunReportVersionPage />
  },
  {
    path: "/report-studio",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_ADMIN_PAGE}><ReportStudioListPage /></NotFoundWrapper>
  },
  {
    path: "/report-studio/schemas/:id",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_ADMIN_PAGE}><SchemaEditorPage /></NotFoundWrapper>
  },
  {
    path: "/report-studio/:id",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_ADMIN_PAGE}><ReportStudioEditorPage /></NotFoundWrapper>
  },
  {
    path: "/reports-v2",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_ADMIN_PAGE}><ReportV2ListPage /></NotFoundWrapper>
  },
  {
    path: "/reports-v2/:id",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_ADMIN_PAGE}><ReportV2FillPage /></NotFoundWrapper>
  },
  {
    path: "/reports-v2/:id/preview",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_ADMIN_PAGE}><ReportV2PreviewPage /></NotFoundWrapper>
  },
  {
    path: "/reports-v2/:id/versions/:versionId",
    element: <NotFoundWrapper permission={FusawarePermission.ACCESS_ADMIN_PAGE}><ReportV2VersionPage /></NotFoundWrapper>
  },
  {
    path: "/projects",
    element: <AllProjects />
  },
  {
    path: "/projects/:projectId",
    element: <ProjectPageLoader><ProjectPage /></ProjectPageLoader>
  },
  {
    path: "/projects/:projectId/requirements",
    element: <ProjectPageLoader><ProjectRequirementsList /></ProjectPageLoader>
  },
  {
    path: "/projects/:projectId/requirements/:requirementId",
    element: <RequirementLoader />
  },
  {
    path: "/projects/:projectId/tasks",
    element: <ProjectPageLoader><ProjectTasksPage /></ProjectPageLoader>
  },
  {
    path: "/projects/:projectId/parts",
    element: <NotFoundWrapper permission={FusawarePermission.MANAGE_PARTS}><ProjectPageLoader><ProjectPartsList /></ProjectPageLoader></NotFoundWrapper>
  },
  {
    path: "/projects/:projectId/milestones",
    element: <ProjectPageLoader><ProjectMilestonesList /></ProjectPageLoader>
  },
  {
    path: "/projects/:projectId/sample-phases",
    element: <ProjectPageLoader><ProjectSamplePhasesList /></ProjectPageLoader>
  },
  {
    path: "/projects/:projectId/templates",
    element: <ProjectPageLoader><ProjectTemplatesPage /></ProjectPageLoader>
  },
  {
    path: "/parts/:id",
    element: <NotFoundWrapper permission={FusawarePermission.MANAGE_PARTS}><PartView /></NotFoundWrapper>
  },
  {
    path: "/procedures/:projectId/:procedureInstanceId/templateProductPage",
    element: <ProductPageLoader type={ProductPageSectionType.TEMPLATE} editable={false} />
  },
  {
    path: "/procedures/:projectId/:procedureInstanceId/procedureProductPage",
    element: <ProductPageLoader type={ProductPageSectionType.PROCEDURE} editable={false} />
  },
  {
    path: "/procedures",
    element: <NotFoundWrapper permission={FusawarePermission.MANAGE_ALL_PROCEDURE_INSTANCES}><ProcedureList /></NotFoundWrapper>
  },
  {
    path: "/procedureCreate/:procedureId",
    element: <ProcedureCreateLoader />
  },
  {
    path: "/procedureImplementationSetup/:procedureId",
    element: <ProcedureImplementationSetupWizardLoader />
  },
  {
    path: "/procedures/edit/:procedureId",
    element: <ProcedureItemLoader><ProcedureItem /></ProcedureItemLoader>
  },
  {
    path: "/procedures/run/:procedureId",
    element: <ProcedureItemLoader><ProcedureRun /></ProcedureItemLoader>
  },
  {
    path: "/fusa-organizations",
    element: <FusaOrganizationCards />
  },
  {
    path: "/fusa-organizations/:fusaOrganizationId",
    element: <FusaOrganizationPage />
  },
  {
    path: "/library",
    element: <Navigate to="/library/project-templates" replace />
  },
  {
    path: "/library/:section",
    element: <LibraryPage />
  },
  {
    path: "/workspaces",
    element: <AllWorkspaces />
  },
  {
    path: "/workspaces/:workspaceId",
    element: <WorkspacePage />
  },
  {
    path: "/support-documentation",
    element: <SupportDocumentationTable />
  }
]; 
